/**
 * Parse Shoutcast / Icecast-style stats XML (SONGTITLE, CURRENTLISTENERS).
 * Shared by the tRPC proxy and optional browser fallback when the server proxy fails.
 */
export type ParsedNowPlaying = {
  artist: string;
  title: string;
  listeners: number;
  raw: string;
};

export type ParsedNowPlayingError = { error: string };

/** Strip bracketed annotations like "[8DX9]", control chars, and stray whitespace. */
export function cleanSongText(value: string): string {
  return value
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/\[[^\]]*\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isUnknownArtist(artist: string): boolean {
  const normalized = artist.toLowerCase();
  return normalized === "unknown" || normalized === "unknown artist";
}

/** Split "Artist - Title" using common dash/hyphen patterns from Shoutcast. */
export function splitArtistTitle(raw: string): {
  artist: string;
  title: string;
} {
  const separators = [" - ", " \u2013 ", " \u2014 "] as const;
  let artist = "";
  let title = raw;
  for (const sep of separators) {
    const idx = raw.indexOf(sep);
    if (idx > 0) {
      artist = raw.slice(0, idx).trim();
      title = raw.slice(idx + sep.length).trim();
      break;
    }
  }
  artist = cleanSongText(artist);
  title = cleanSongText(title);
  if (isUnknownArtist(artist)) artist = "";
  return { artist, title };
}

export function parseShoutcastStatsXml(
  text: string
): ParsedNowPlaying | ParsedNowPlayingError {
  const songMatch = text.match(/<SONGTITLE>(.*?)<\/SONGTITLE>/i);
  const listenersMatch = text.match(
    /<CURRENTLISTENERS>(.*?)<\/CURRENTLISTENERS>/i
  );
  if (!songMatch) return { error: "No song title found" };
  const raw = songMatch[1]
    .trim()
    .replace(/&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
  const { artist, title } = splitArtistTitle(raw);
  const listeners = listenersMatch ? parseInt(listenersMatch[1], 10) : 0;
  return {
    artist,
    title,
    listeners: Number.isFinite(listeners) ? listeners : 0,
    raw,
  };
}
